import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { LineMask, FadeIn } from "@/components/Reveal";
import { services } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  organisation: "",
  email: "",
  service: "",
  message: "",
};

const inputClass =
  "w-full bg-transparent border-0 border-b border-ink/25 px-0 py-4 text-base text-ink placeholder:text-ink/30 focus:border-brand focus:outline-none focus:ring-0 transition-colors duration-300 rounded-none";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent — thank you. We'll respond within 5 working days.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-paper pt-44 pb-28 lg:pb-40" data-testid="contact-section">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <FadeIn>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                Contact
              </p>
            </FadeIn>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink">
              <LineMask delay={0.2} lines={["Start a", "conversation."]} />
            </h1>
            <FadeIn delay={0.45} className="mt-8 max-w-md">
              <p className="text-base leading-relaxed text-ink/65">
                Tell us about your service and what you're working towards. Every
                enquiry is treated in confidence, and we aim to respond within five
                working days.
              </p>
            </FadeIn>

            <FadeIn delay={0.6} className="mt-14">
              <dl className="border-t border-ink/10" data-testid="contact-details">
                <div className="grid grid-cols-3 gap-4 border-b border-ink/10 py-6">
                  <dt className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue pt-1">Email</dt>
                  <dd className="col-span-2 text-sm text-ink">
                    <a href="mailto:info@kcconsultancy.co.uk" className="link-underline" data-testid="contact-email-link">
                      info@kcconsultancy.co.uk
                    </a>
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-4 border-b border-ink/10 py-6">
                  <dt className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue pt-1">Telephone</dt>
                  <dd className="col-span-2 text-sm text-ink" data-testid="contact-phone">
                    <a href="tel:+447904805795" className="link-underline">07904 805 795</a>
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-4 border-b border-ink/10 py-6">
                  <dt className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue pt-1">Coverage</dt>
                  <dd className="col-span-2 text-sm text-ink" data-testid="contact-coverage">England &amp; Wales</dd>
                </div>
                <div className="grid grid-cols-3 gap-4 border-b border-ink/10 py-6">
                  <dt className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue pt-1">Response</dt>
                  <dd className="col-span-2 text-sm text-ink" data-testid="contact-response">Within 5 working days</dd>
                </div>
                <div className="grid grid-cols-3 gap-4 border-b border-ink/10 py-6">
                  <dt className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue pt-1">Your contact</dt>
                  <dd className="col-span-2 text-sm text-ink" data-testid="contact-founder">Founder &amp; Principal Consultant — every enquiry is answered personally</dd>
                </div>
              </dl>
            </FadeIn>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <FadeIn delay={0.35}>
              <form
                onSubmit={handleSubmit}
                className="border border-ink/10 bg-white p-8 sm:p-12"
                data-testid="contact-form"
              >
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue">
                  Enquiry form
                </p>

                <div className="mt-8 space-y-8">
                  <div>
                    <label htmlFor="name" className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink/50">
                      Full name *
                    </label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className={inputClass}
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="organisation" className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink/50">
                      Organisation
                    </label>
                    <input
                      id="organisation"
                      type="text"
                      value={form.organisation}
                      onChange={update("organisation")}
                      placeholder="Local authority or organisation"
                      className={inputClass}
                      data-testid="contact-organisation-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink/50">
                      Email *
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.gov.uk"
                      className={inputClass}
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink/50">
                      Service of interest
                    </label>
                    <select
                      id="service"
                      value={form.service}
                      onChange={update("service")}
                      className={`${inputClass} cursor-pointer`}
                      data-testid="contact-service-select"
                    >
                      <option value="">Select a service (optional)</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.title}>{s.title}</option>
                      ))}
                      <option value="General enquiry">General enquiry</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink/50">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us a little about your service and what you need"
                      className={`${inputClass} resize-none`}
                      data-testid="contact-message-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="mt-12 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-ink px-10 py-4 text-sm tracking-wide text-paper transition-colors duration-300 hover:bg-brand disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      Sending
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Send enquiry
                      <ArrowUpRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <p className="mt-6 text-xs leading-relaxed text-ink/40">
                  Your details are used only to respond to your enquiry and are never
                  shared with third parties.
                </p>
              </form>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
